import { useCallback, RefObject } from "react";
import { TransactionDetailValue, TransactionCustomFieldValues } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";

interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string; // Rate from transaction currency to default currency
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: TransactionDetailValue;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface UseDialogHandlersProps {
  defaultCurrency: string;
  setDialogOpen: (open: boolean) => void;
  setTransferDialogOpen: (open: boolean) => void;
  setEditingTransaction: (transaction: TransactionWithDate | null) => void;
  setInitialFormData: (data: FormData | null) => void;
  setInitialTransferFormData: (data: TransferFormData | null) => void;
  setIsSubmitting: (submitting: boolean) => void;
  setIsSubmittingTransfer: (submitting: boolean) => void;
  setCurrencyMenuAnchorEl: (el: HTMLElement | null) => void;
  setFormData: (data: FormData | ((prev: FormData) => FormData)) => void;
  setTransferFormData: (data: TransferFormData | ((prev: TransferFormData) => TransferFormData)) => void;
  setShowTime: (show: boolean) => void;
  setShowTransferTime: (show: boolean) => void;
  setShowTransferFee?: (show: boolean) => void;
  setRateFromTransaction: (fromTransaction: boolean) => void;
  setRateManuallyEdited: (edited: boolean) => void;
  setTransferRateManuallyEdited: (edited: boolean) => void;
  setNewlyAddedDetailOptions: (options: Record<string, string[]> | ((prev: Record<string, string[]>) => Record<string, string[]>)) => void;
  setDetailInputValues: (values: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
}

export const useDialogHandlers = ({
  defaultCurrency,
  setDialogOpen,
  setTransferDialogOpen,
  setEditingTransaction,
  setInitialFormData,
  setInitialTransferFormData,
  setIsSubmitting,
  setIsSubmittingTransfer,
  setCurrencyMenuAnchorEl,
  setFormData,
  setTransferFormData,
  setShowTime,
  setShowTransferTime,
  setRateFromTransaction,
  setRateManuallyEdited,
  setTransferRateManuallyEdited,
  setNewlyAddedDetailOptions,
  setDetailInputValues,
}: UseDialogHandlersProps) => {
  const handleDialogClose = useCallback((): void => {
    setDialogOpen(false);
    setInitialFormData(null);
    setEditingTransaction(null);
    setIsSubmitting(false);
    setCurrencyMenuAnchorEl(null);
    // Clear newly added detail options when closing dialog
    setNewlyAddedDetailOptions({});
    setDetailInputValues({});
    setFormData({
      title: "",
      category: "",
      amount: "",
      currency: defaultCurrency,
      rate: "",
      defaultCurrencyRate: "",
      date: new Date().toISOString().split("T")[0],
      time: "",
      accountId: "",
      accountGroup: "",
      customFieldValues: undefined,
      customFieldPendingFiles: undefined,
    });
    setShowTime(false);
    // Reset rate tracking flags
    setRateFromTransaction(false);
    setRateManuallyEdited(false);
  }, [
    defaultCurrency,
    setDialogOpen,
    setInitialFormData,
    setEditingTransaction,
    setIsSubmitting,
    setCurrencyMenuAnchorEl,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
    setFormData,
    setShowTime,
    setRateFromTransaction,
    setRateManuallyEdited,
  ]);

  const handleTransferDialogClose = useCallback((): void => {
    setTransferDialogOpen(false);
    setEditingTransaction(null);
    setInitialTransferFormData(null);
    setIsSubmittingTransfer(false);
    setShowTransferTime(false);
    setTransferFormData({
      fromAccountId: "",
      toAccountId: "",
      amount: "",
      date: new Date().toISOString().split("T")[0],
      time: "",
      transferRate: "",
      customFieldValues: undefined,
      customFieldPendingFiles: undefined,
    });
    // Reset transfer rate tracking flag
    setTransferRateManuallyEdited(false);
  }, [
    setTransferDialogOpen,
    setEditingTransaction,
    setInitialTransferFormData,
    setIsSubmittingTransfer,
    setShowTransferTime,
    setTransferFormData,
    setTransferRateManuallyEdited,
  ]);

  return {
    handleDialogClose,
    handleTransferDialogClose,
  };
};
